// PartnerBrandsMobileComponent.js
import React from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LinkWithIconComponent from '../LinkWithIcon/LinkWithIconComponent';
import Image from '../../Image/Image';

const MOBILE_GAP = 16;

// Defined at module scope so keyframe names are stable across re-renders.
const scrollLeft = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-33.3333%); }
`;

const scrollRight = keyframes`
  0%   { transform: translateX(-33.3333%); }
  100% { transform: translateX(0); }
`;

const PartnerBrandsMobileComponent = ({ data }) => {
  const navigate = useNavigate();
  const {
    title,
    description,
    footer,
    footerLinkDescription,
    footerLink,
    logos = [],
  } = data;

  // Both rows get all logos, independently shuffled so they look different
  const { top, bottom } = React.useMemo(() => {
    if (!logos || logos.length === 0) {
      return { top: [], bottom: [] };
    }
    const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);
    return { top: shuffle(logos), bottom: shuffle(logos) };
  }, [logos]);

  const logoBox = {
    width: '160px',
    height: '88px',
    backgroundColor: '#FFFFFF',
    borderRadius: '24px',
    padding: '20px 16px',
    boxShadow: '0px 6px 16px 0px #162F3B1F, 0px 1px 2px 0px #A2ACB11F',
    position: 'relative',
    overflow: 'visible',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };

  // Full-bleed wrapper so rows span the screen
  const bleedWrapper = {
    position: 'relative',
    width: '100vw',
    maxWidth: '100vw',
    marginLeft: 'calc(-50vw + 50%)',
    marginRight: 'calc(-50vw + 50%)',
    boxSizing: 'border-box',
    overflowX: 'visible',
    overflowY: 'visible',
    paddingLeft: 'env(safe-area-inset-left)',
    paddingRight: 'env(safe-area-inset-right)',
  };

  const rowBase = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: 'fit-content',
    willChange: 'transform',
    gap: `${MOBILE_GAP}px`,
    // paddingRight equal to gap so -50% lands exactly at the repeat seam
    paddingRight: `${MOBILE_GAP}px`,
  };

  const renderLogoCard = (logo, key, paddingBottom) => {
    const isClickable = Boolean(logo?.url);
    const Component = isClickable ? 'button' : 'div';

    return (
      <Box key={key} sx={{ position: 'relative', pb: paddingBottom }}>
        <Box
          component={Component}
          type={isClickable ? 'button' : undefined}
          onClick={isClickable ? () => navigate(logo.url) : undefined}
          aria-label={
            isClickable
              ? `View ${logo.imageAltTitle || logo.imgAltTag}`
              : undefined
          }
          sx={{
            ...logoBox,
            appearance: 'none',
            border: 'none',
            cursor: isClickable ? 'pointer' : 'default',
            font: 'inherit',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': isClickable
              ? {
                  transform: 'translateY(-2px)',
                  boxShadow:
                    '0px 10px 22px 0px rgba(22, 47, 59, 0.16), 0px 2px 6px 0px rgba(162, 172, 177, 0.16)',
                }
              : undefined,
          }}
        >
          {logo && (
            <Image
              src={logo.logo}
              alt={logo.imgAltTag || key}
              title={logo.imageAltTitle || ''}
              width={160}
              height={88}
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
              }}
            />
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100vw',
        backgroundColor: '#E3E6E8',
        borderRadius: '48px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        boxSizing: 'border-box',

        overflow: 'visible',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: 'auto',
          pt: '32px',
          pb: '16px',
          backgroundColor: '#E3E6E8',
          borderRadius: '48px',
          boxSizing: 'border-box',
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
        }}
      >
        {/* Title */}
        {title && (
          <Typography
            component="h2"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '2rem',
              lineHeight: '2.125rem',
              letterSpacing: '-0.03em',
              textAlign: 'center',
              color: '#162F3B',
              px: '16px',
            }}
          >
            {title}
          </Typography>
        )}

        {/* Description */}
        {description && (
          <Typography
            component={'p'}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '1.25rem',
              lineHeight: '1.5rem',
              letterSpacing: '-0.03em',
              textAlign: 'center',
              color: '#162F3B',
              mb: '20px',
              px: '16px',
              alignSelf: 'stretch',
              boxSizing: 'border-box',
              whiteSpace: 'pre-line',
            }}
          >
            {description.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
              /^\*\*[^*]+\*\*$/.test(part) ? (
                <strong key={i} style={{ fontWeight: 700 }}>
                  {part.slice(2, -2)}
                </strong>
              ) : (
                <span key={i}>{part}</span>
              )
            )}
          </Typography>
        )}

        {/* Full-bleed ticker */}
        <Box sx={bleedWrapper}>
          {/* Top Row */}
          {top.length > 0 &&
            (() => {
              const MIN = 7;
              const padSeamless = (src) => {
                if (src.length === 0 || src.length >= MIN) return src;
                if (src.length === 1) {
                  const o = [];
                  while (o.length < MIN) o.push(src[0]);
                  return o;
                }
                const n = src.length;
                const out = [...src];
                let i = 0;
                while (out.length < MIN || out[out.length - 1] === out[0]) {
                  const c = src[i % n];
                  i++;
                  if (c !== out[out.length - 1]) out.push(c);
                  if (i > MIN * n * 3) break;
                }
                return out;
              };
              const copy = padSeamless(top);
              const duration = copy.length * 5;
              return (
                <Box
                  sx={{
                    width: '100%',
                    overflow: 'hidden',
                    py: '10px',
                    my: '-20px',
                  }}
                >
                  <Box
                    sx={{
                      ...rowBase,
                      animation: `${scrollRight} ${duration}s linear infinite`,
                    }}
                  >
                    {[...copy, ...copy, ...copy].map((logo, i) =>
                      renderLogoCard(logo, `top-${i}`, '16px')
                    )}
                  </Box>
                </Box>
              );
            })()}

          {/* Bottom Row */}
          {bottom.length > 0 &&
            (() => {
              const MIN = 7;
              const padSeamless = (src) => {
                if (src.length === 0 || src.length >= MIN) return src;
                if (src.length === 1) {
                  const o = [];
                  while (o.length < MIN) o.push(src[0]);
                  return o;
                }
                const n = src.length;
                const out = [...src];
                let i = 0;
                while (out.length < MIN || out[out.length - 1] === out[0]) {
                  const c = src[i % n];
                  i++;
                  if (c !== out[out.length - 1]) out.push(c);
                  if (i > MIN * n * 3) break;
                }
                return out;
              };
              const copy = padSeamless(bottom);
              const duration = copy.length * 5;
              return (
                <Box
                  sx={{
                    width: '100%',
                    overflow: 'hidden',
                    py: '10px',
                    my: '-20px',
                  }}
                >
                  <Box
                    sx={{
                      ...rowBase,
                      animation: `${scrollLeft} ${duration}s linear infinite`,
                    }}
                  >
                    {[...copy, ...copy, ...copy].map((logo, i) =>
                      renderLogoCard(logo, `bottom-${i}`, '12px')
                    )}
                  </Box>
                </Box>
              );
            })()}
        </Box>

        {/* Footer */}
        {footer && (
          <Box
            sx={{
              px: '16px',
              pb: { xs: '8px', sm: '0px' },
              boxSizing: 'border-box',
              alignSelf: 'stretch',
            }}
          >
            <Typography
              component={'p'}
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '1rem',
                lineHeight: '1.5rem',
                letterSpacing: '-0.002em',
                textAlign: 'center',
                color: '#162F3B',
                whiteSpace: 'pre-line',
              }}
            >
              {footer}
            </Typography>
          </Box>
        )}
        {footerLinkDescription && (
          <LinkWithIconComponent
            href={footerLink || undefined}
            iconSize="1rem"
            sx={{
              mb: '15px',
              px: '16px',
              pb: '8px',
              textAlign: 'center',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '1.25rem',
              lineHeight: '1.5rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
              boxSizing: 'border-box',
              alignSelf: 'stretch',
            }}
          >
            {footerLinkDescription}
          </LinkWithIconComponent>
        )}
      </Box>
    </Box>
  );
};

export default PartnerBrandsMobileComponent;
